import { forwardRef } from 'react';
import { VariantProps, cva } from 'class-variance-authority';
import { cn } from '@/utils/tailwind';

export const inputVariants = cva(
  'flex w-full text-gr-800 focus-visible:outline-none placeholder:text-gr-400 autofill:text-gr-800',
  {
    variants: {
      variant: {
        default: 'border-gr-300 border h-11 p-2.5 rounded-[8px] Body2M16',
        underline: 'py-[13px] border-b-[1px] border-v3-gr-600 rounded-[0px] Body5S14',
      },
    },
  },
);

export type InputProps = React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> &
  VariantProps<typeof inputVariants> & {
    textarea?: boolean;
    /** 인풋 내부 우측에 들어갈 컨텐츠 */
    InputProps?: {
      endAdornment?: React.ReactNode;
      endAdornmentStyle?: string;
    };
    Error?: {
      isError?: boolean;
      errorMessage?: string;
      component?: React.ReactNode;
    };
  };

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    { className, textarea = false, variant = 'default', type, InputProps, Error, ...props },
    ref,
  ) => {
    return (
      <div className="w-full">
        <div className="relative w-full">
          {textarea ? (
            <textarea
              className={cn(inputVariants({ variant, className }))}
              ref={ref as React.Ref<HTMLTextAreaElement>}
              {...props}
            />
          ) : (
            <input
              type={type}
              className={cn(inputVariants({ variant, className }), {
                'border-red-400 ': Error?.isError,
              })}
              ref={ref as React.Ref<HTMLInputElement>}
              {...props}
            />
          )}
          {InputProps?.endAdornment && (
            <div
              className={cn(
                'absolute right-2 top-1/2 m-auto flex -translate-y-1/2 transform items-center whitespace-nowrap',
                InputProps?.endAdornmentStyle,
              )}
            >
              {InputProps.endAdornment}
            </div>
          )}
        </div>
        {Error?.isError && (
          <div className="mt-2 flex items-center justify-between">
            <div className=" text-left text-red-400 Caption1M12">{Error?.errorMessage}</div>
            {Error.component}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
